import { useMemo } from 'react';
import {
  getRacesForRuleset,
  getRaceForRuleset,
  getClassesForRuleset,
  getClassForRuleset,
  getBackgroundsForRuleset,
  getBackgroundForRuleset,
  getFeatsForRuleset,
  getSpellsForRuleset,
  getRaceWithSubraceForRuleset,
} from '@character-creator/data';
import type { Race, CharacterClass, Background, FeatDefinition, Spell, Subrace } from '@character-creator/types';
import { useCharacterCreationStore } from '../stores/characterCreationStore';

/**
 * Hook to get all races for the current ruleset
 */
export function useRulesetRaces(): Race[] {
  const rulesetId = useCharacterCreationStore((s) => s.draft.rulesetId);
  return useMemo(() => getRacesForRuleset(rulesetId), [rulesetId]);
}

/**
 * Hook to get a specific race for the current ruleset
 */
export function useRulesetRace(raceId: string | null): Race | undefined {
  const rulesetId = useCharacterCreationStore((s) => s.draft.rulesetId);
  return useMemo(() => {
    if (!raceId) return undefined;
    return getRaceForRuleset(raceId, rulesetId);
  }, [raceId, rulesetId]);
}

/**
 * Hook to get a race with optional subrace for the current ruleset
 */
export function useRulesetRaceWithSubrace(
  raceId: string | null,
  subraceId: string | null
): { race: Race; subrace?: Subrace } | undefined {
  const rulesetId = useCharacterCreationStore((s) => s.draft.rulesetId);
  return useMemo(() => {
    if (!raceId) return undefined;
    return getRaceWithSubraceForRuleset(raceId, subraceId, rulesetId);
  }, [raceId, subraceId, rulesetId]);
}

/**
 * Hook to get all classes for the current ruleset
 */
export function useRulesetClasses(): CharacterClass[] {
  const rulesetId = useCharacterCreationStore((s) => s.draft.rulesetId);
  return useMemo(() => getClassesForRuleset(rulesetId), [rulesetId]);
}

/**
 * Hook to get a specific class for the current ruleset
 */
export function useRulesetClass(classId: string | null): CharacterClass | undefined {
  const rulesetId = useCharacterCreationStore((s) => s.draft.rulesetId);
  return useMemo(() => {
    if (!classId) return undefined;
    return getClassForRuleset(classId, rulesetId);
  }, [classId, rulesetId]);
}

/**
 * Hook to get all backgrounds for the current ruleset
 */
export function useRulesetBackgrounds(): Background[] {
  const rulesetId = useCharacterCreationStore((s) => s.draft.rulesetId);
  return useMemo(() => getBackgroundsForRuleset(rulesetId), [rulesetId]);
}

/**
 * Hook to get a specific background for the current ruleset
 */
export function useRulesetBackground(backgroundId: string | null): Background | undefined {
  const rulesetId = useCharacterCreationStore((s) => s.draft.rulesetId);
  return useMemo(() => {
    if (!backgroundId) return undefined;
    return getBackgroundForRuleset(backgroundId, rulesetId);
  }, [backgroundId, rulesetId]);
}

/**
 * Hook to get all feats for the current ruleset
 */
export function useRulesetFeats(): FeatDefinition[] {
  const rulesetId = useCharacterCreationStore((s) => s.draft.rulesetId);
  return useMemo(() => getFeatsForRuleset(rulesetId), [rulesetId]);
}

/**
 * Hook to get all spells for the current ruleset
 */
export function useRulesetSpells(): Spell[] {
  const rulesetId = useCharacterCreationStore((s) => s.draft.rulesetId);
  return useMemo(() => getSpellsForRuleset(rulesetId), [rulesetId]);
}
